import React, { useState, forwardRef, useEffect, useMemo } from 'react';
import { LabService, type LabMember, type LabMemberCandidate } from '../services/labService';
import { MemberRemovalModal, type MemberRemovalModalRef } from './MemberRemovalModal';

interface LabDetailsModalProps {
  labId: string;
  labName: string;
  isAdmin: boolean;
  onClose: () => void;
  onMemberRemoved?: () => void;
  actorId: string | null;
  actorName: string;
  organisationId: string | null;
}

export interface LabDetailsModalRef {
  showModal: () => void;
  close: () => void;
}

export const LabDetailsModal = forwardRef<LabDetailsModalRef, LabDetailsModalProps>(({
  labId,
  labName,
  isAdmin,
  onClose,
  onMemberRemoved,
  actorId,
  actorName,
  organisationId
}, ref) => {
  const [members, setMembers] = useState<LabMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [candidateLoading, setCandidateLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [candidateError, setCandidateError] = useState<string | null>(null);
  const [memberToRemove, setMemberToRemove] = useState<{ member: LabMember | null, labName: string }>({ member: null, labName: '' });
  const [message, setMessage] = useState<{ text: string; type: 'error' | 'success' }>({ text: '', type: 'error' });
  const [removalReason, setRemovalReason] = useState<string>('');
  const [notifyUser, setNotifyUser] = useState<boolean>(false);
  const [memberCandidates, setMemberCandidates] = useState<LabMemberCandidate[]>([]);
  const [addingMemberId, setAddingMemberId] = useState<string | null>(null);
  const [assigningChampionId, setAssigningChampionId] = useState<string | null>(null);
  const [isAddMemberModalOpen, setIsAddMemberModalOpen] = useState(false);
  const [candidateSearchTerm, setCandidateSearchTerm] = useState('');

  const dialogRef = React.useRef<HTMLDialogElement>(null);
  const addMemberDialogRef = React.useRef<HTMLDialogElement>(null);
  const memberRemovalModalRef = React.useRef<MemberRemovalModalRef>(null);
  const canManageMembers = isAdmin;

  React.useImperativeHandle(ref, () => ({
    showModal: () => {
      dialogRef.current?.showModal();
      void refreshModalData();
    },
    close: () => dialogRef.current?.close()
  }));

  const fetchMembers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await LabService.getLabMembers(labId);
      const allMembers: LabMember[] = Array.isArray(response.members) ? response.members : [];
      setMembers(allMembers);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load members';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const fetchMemberCandidates = async () => {
    if (!canManageMembers) {
      setMemberCandidates([]);
      setCandidateError(null);
      setCandidateLoading(false);
      return;
    }

    setCandidateLoading(true);
    setCandidateError(null);
    try {
      const response = await LabService.getLabMemberCandidates(labId);
      const candidates: LabMemberCandidate[] = Array.isArray(response.candidates)
        ? response.candidates
        : [];
      setMemberCandidates(candidates);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load organisation members';
      setCandidateError(errorMessage);
      setMemberCandidates([]);
    } finally {
      setCandidateLoading(false);
    }
  };

  const refreshModalData = async () => {
    await Promise.all([
      fetchMembers(),
      fetchMemberCandidates(),
    ]);
  };

  const handleRemoveMemberClick = (member: LabMember): void => {
    setRemovalReason('');
    setNotifyUser(false);
    setMemberToRemove({
      member,
      labName: labName
    });
  };

  useEffect(() => {
    if (memberToRemove.member) {
      memberRemovalModalRef.current?.showModal();
    }
  }, [memberToRemove.member]);

  useEffect(() => {
    const dialog = addMemberDialogRef.current;
    if (!dialog) {
      return;
    }

    if (isAddMemberModalOpen && !dialog.open) {
      dialog.showModal();
    }

    if (!isAddMemberModalOpen && dialog.open) {
      dialog.close();
    }
  }, [isAddMemberModalOpen]);

  const handleConfirmRemoveMember = async (): Promise<void> => {
    if (!memberToRemove.member) return;

    try {
      await LabService.removeLabMember(labId, memberToRemove.member.id, {
        reason: removalReason.trim() || undefined,
        notifyUser,
        actorId,
        actorName,
        removedUserId: memberToRemove.member.id,
        organisationId,
        labName,
        timestamp: new Date().toISOString(),
      });

      // Show success message
      const memberName = [memberToRemove.member.first_name, memberToRemove.member.last_name]
        .filter(Boolean).join(' ') || memberToRemove.member.email;
      setMessage({
        text: `Removed ${memberName} from ${labName}.`,
        type: 'success'
      });

      // Refresh members list
      await refreshModalData();

      // Notify parent component
      if (onMemberRemoved) {
        onMemberRemoved();
      }

      // Reset state
      setMemberToRemove({ member: null, labName: '' });
      setRemovalReason('');
      setNotifyUser(false);

      // Clear message after 3 seconds
      setTimeout(() => setMessage({ text: '', type: 'error' }), 3000);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to remove member';
      setMessage({ text: errorMessage, type: 'error' });
    }
  };

  const handleLeaveLab = async (): Promise<void> => {
    try {
      const result = await LabService.leaveLab(labId);

      setMessage({
        text: result.message || `Successfully left ${labName}.`,
        type: 'success',
      });

      await refreshModalData();

      if (onMemberRemoved) {
        onMemberRemoved();
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to leave lab';
      setMessage({ text: errorMessage, type: 'error' });
    }
  };

  const handleAddMember = async (candidate: LabMemberCandidate): Promise<void> => {
    if (!candidate.can_add) {
      return;
    }

    setAddingMemberId(candidate.id);
    try {
      await LabService.addLabMember(labId, candidate.id);

      const memberName = [candidate.first_name, candidate.last_name]
        .filter(Boolean)
        .join(' ') || candidate.email;

      setMessage({
        text: `Added ${memberName} to ${labName}.`,
        type: 'success',
      });

      await refreshModalData();
      setIsAddMemberModalOpen(false);
      setCandidateSearchTerm('');

      if (onMemberRemoved) {
        onMemberRemoved();
      }

      setTimeout(() => setMessage({ text: '', type: 'error' }), 3000);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to add member';
      setMessage({ text: errorMessage, type: 'error' });
    } finally {
      setAddingMemberId(null);
    }
  };

  const handleAssignChampion = async (member: LabMember): Promise<void> => {
    setAssigningChampionId(member.id);
    try {
      const result = await LabService.assignChampion(labId, member.id);
      const championName = [member.first_name, member.last_name]
        .filter(Boolean)
        .join(' ') || member.email;

      setMessage({
        text: result.message || `Made ${championName} the champion of ${labName}.`,
        type: 'success',
      });

      await refreshModalData();

      if (onMemberRemoved) {
        onMemberRemoved();
      }

      setTimeout(() => setMessage({ text: '', type: 'error' }), 3000);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to transfer champion status';
      setMessage({ text: errorMessage, type: 'error' });
    } finally {
      setAssigningChampionId(null);
    }
  };

  const getCandidateHelperText = (candidate: LabMemberCandidate): string => {
    if (candidate.disabled_reason === 'in_other_lab' && candidate.current_lab_name) {
      return `in the ${candidate.current_lab_name}`;
    }

    if (candidate.disabled_reason === 'already_in_lab') {
      return 'already in this lab';
    }

    return 'click to add';
  };

  const handleOpenAddMemberModal = async (): Promise<void> => {
    setCandidateSearchTerm('');
    setCandidateError(null);
    setIsAddMemberModalOpen(true);
    await fetchMemberCandidates();
  };

  const filteredCandidates = useMemo(() => {
    const query = candidateSearchTerm.trim().toLowerCase();
    if (!query) {
      return memberCandidates;
    }

    return memberCandidates.filter((candidate) => {
      const displayName = [candidate.first_name, candidate.last_name]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      const email = candidate.email.toLowerCase();
      const currentLabName = candidate.current_lab_name?.toLowerCase() ?? '';

      return displayName.includes(query) || email.includes(query) || currentLabName.includes(query);
    });
  }, [candidateSearchTerm, memberCandidates]);

  const downloadCSV = () => {
    const headers = ['Name', 'Email', 'Role', 'Last Active'];
    const rows = members.map(member => {
      const nameParts = [member.first_name, member.last_name].filter(Boolean);
      const displayName = nameParts.length > 0 ? nameParts.join(' ') : '';
      const roleLabel = member.is_admin ? 'Admin' : member.is_champion ? 'L0 Champion' : 'Member';
      return [
        displayName,
        member.email,
        roleLabel,
        member.last_active || 'N/A'
      ];
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${labName.replace(/\s+/g, '_')}_members.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <dialog ref={dialogRef} className="modal">
        <div className="modal-box max-w-4xl">
          <h3 className="font-bold text-lg mb-4">Lab Members - {labName}</h3>

          {/* Success/Error Message */}
          {message.text && (
            <div className={`alert ${message.type === 'success' ? 'alert-success' : 'alert-error'} mb-4`}>
              <span>{message.text}</span>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="flex justify-center py-8">
              <span className="loading loading-spinner loading-lg"></span>
            </div>
          )}

          {/* Error State */}
          {error && !loading && (
            <div className="alert alert-error">
              <span>{error}</span>
            </div>
          )}

          {/* Members List */}
          {!loading && !error && (
            <>
              <div className="flex justify-between items-center mb-4">
                <p className="text-sm text-base-content/70">
                  Total members: {members.length}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    className="btn btn-sm btn-outline"
                    onClick={downloadCSV}
                    disabled={members.length === 0}
                  >
                    Download CSV
                  </button>
                  {canManageMembers && (
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      onClick={() => void handleOpenAddMemberModal()}
                    >
                      Add member
                    </button>
                  )}
                </div>
              </div>

              {members.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="table w-full">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Role</th>
                        {/* <th>Last Active</th> */}
                        {isAdmin && <th>Actions</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {members.map((member) => {
                        const nameParts = [member.first_name, member.last_name].filter(Boolean);
                        const displayName = nameParts.length > 0 ? nameParts.join(' ') : '—';
                        const roleLabel = member.is_admin ? 'Admin' : member.is_champion ? 'L0 Champion' : 'Member';
                        const isCurrentUser = actorId === member.id;

                        return (
                          <tr key={member.id}>
                            <td>{displayName}</td>
                            <td>{member.email}</td>
                            <td>
                              <span className={`badge ${member.is_admin ? 'badge-error' :
                                  member.is_champion ? 'badge-primary' :
                                    'badge-ghost'
                                }`}>
                                {roleLabel}
                              </span>
                            </td>
                            {/* <td>{member.last_active || 'N/A'}</td> */}
                            {isAdmin && (
                              <td>
                                {isCurrentUser ? (
                                  <div className="flex flex-wrap gap-2">
                                    {!member.is_champion && (
                                      <button
                                        className="btn btn-sm btn-outline"
                                        onClick={() => void handleAssignChampion(member)}
                                        disabled={assigningChampionId === member.id}
                                      >
                                        {assigningChampionId === member.id ? 'Saving...' : 'Make champion'}
                                      </button>
                                    )}
                                    <button
                                      className="btn btn-sm btn-outline"
                                      onClick={() => void handleLeaveLab()}
                                      disabled={assigningChampionId === member.id}
                                    >
                                      Leave
                                    </button>
                                  </div>
                                ) : (
                                  <div className="flex flex-wrap gap-2">
                                    {!member.is_champion && (
                                      <button
                                        className="btn btn-sm btn-outline"
                                        onClick={() => void handleAssignChampion(member)}
                                        disabled={assigningChampionId === member.id}
                                      >
                                        {assigningChampionId === member.id ? 'Saving...' : 'Make champion'}
                                      </button>
                                    )}
                                    <button
                                      className="btn btn-sm btn-outline btn-error"
                                      onClick={() => handleRemoveMemberClick(member)}
                                      disabled={assigningChampionId === member.id}
                                    >
                                      Remove
                                    </button>
                                  </div>
                                )}
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-base-content/70">No members found in this lab.</p>
                </div>
              )}
            </>
          )}

          <div className="modal-action">
            <button
              className="btn"
              onClick={() => {
                onClose();
                dialogRef.current?.close();
              }}
            >
              Close
            </button>
          </div>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button onClick={onClose}>close</button>
        </form>
      </dialog>

      <dialog ref={addMemberDialogRef} className="modal" onClose={() => setIsAddMemberModalOpen(false)}>
        <div className="modal-box max-w-4xl">
          <div className="mb-4 flex items-start justify-between gap-4">
            <div>
              <h3 className="font-bold text-lg">Add Member</h3>
              <p className="text-sm text-base-content/70">
                Search your organisation and click a user row to add them to {labName} immediately.
              </p>
            </div>
            <button
              type="button"
              className="btn btn-sm btn-ghost"
              onClick={() => setIsAddMemberModalOpen(false)}
            >
              Close
            </button>
          </div>

          <label className="input input-bordered mb-4 flex items-center gap-2">
            <input
              type="text"
              className="grow"
              placeholder="Search by name, email, or current lab"
              value={candidateSearchTerm}
              onChange={(event) => setCandidateSearchTerm(event.target.value)}
            />
          </label>

          {candidateError && (
            <div className="alert alert-error mb-4">
              <span>{candidateError}</span>
            </div>
          )}

          {candidateLoading ? (
            <div className="flex justify-center py-10">
              <span className="loading loading-spinner loading-lg"></span>
            </div>
          ) : filteredCandidates.length > 0 ? (
            <div className="max-h-[28rem] overflow-y-auto">
              <table className="table table-zebra w-full">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCandidates.map((candidate) => {
                    const nameParts = [candidate.first_name, candidate.last_name].filter(Boolean);
                    const displayName = nameParts.length > 0 ? nameParts.join(' ') : '—';
                    const isAdding = addingMemberId === candidate.id;

                    return (
                      <tr
                        key={candidate.id}
                        className={candidate.can_add ? 'cursor-pointer hover:bg-base-200' : 'opacity-60'}
                        onClick={() => {
                          if (candidate.can_add && addingMemberId === null) {
                            void handleAddMember(candidate);
                          }
                        }}
                      >
                        <td>
                          <div className="font-medium">{displayName}</div>
                        </td>
                        <td>{candidate.email}</td>
                        <td>
                          <span className={`text-sm ${candidate.can_add ? 'text-primary font-medium' : 'text-base-content/70'}`}>
                            {isAdding ? 'Adding...' : getCandidateHelperText(candidate)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-base-300 px-4 py-10 text-center">
              <p className="font-medium text-base-content">
                {candidateSearchTerm ? 'No matching users found' : 'No organisation members available'}
              </p>
            </div>
          )}
        </div>
        <form method="dialog" className="modal-backdrop">
          <button onClick={() => setIsAddMemberModalOpen(false)}>close</button>
        </form>
      </dialog>

      {/* Member Removal Modal */}
      {memberToRemove.member && (
        <MemberRemovalModal
          ref={memberRemovalModalRef}
          memberName={[memberToRemove.member.first_name, memberToRemove.member.last_name]
            .filter(Boolean).join(' ') || memberToRemove.member.email}
          labName={memberToRemove.labName}
          reason={removalReason}
          onReasonChange={setRemovalReason}
          notifyUser={notifyUser}
          onNotifyUserChange={setNotifyUser}
          handleRemoveMember={handleConfirmRemoveMember}
          handleCancel={() => {
            setMemberToRemove({ member: null, labName: '' });
            setRemovalReason('');
            setNotifyUser(false);
          }}
        />
      )}
    </>
  );
});
LabDetailsModal.displayName = 'LabDetailsModal';
